/**
 * API Credit Display Component
 *
 * Displays API credit information with a progress bar
 */

import { IconCoins } from '@tabler/icons-react'
import { Progress } from '@/components/ui/progress'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface ApiCreditDisplayProps {
  balance: number
  limit?: number
  showIcon?: boolean
  showProgress?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export default function ApiCreditDisplay({
  balance,
  limit,
  showIcon = true,
  showProgress = true,
  className = '',
  size = 'md'
}: ApiCreditDisplayProps) {
  const { t } = useTranslation()

  // Calculate percentage if limit is provided
  const percentage = limit ? Math.min(Math.round((balance / limit) * 100), 100) : null

  // Determine color based on percentage
  const getProgressColor = () => {
    if (!percentage) return 'bg-primary'
    if (percentage < 25) return 'bg-red-500'
    if (percentage < 50) return 'bg-amber-500'
    return 'bg-green-500'
  }

  // Size classes
  const sizeClasses = {
    sm: {
      container: 'text-sm',
      icon: 'w-4 h-4',
      balance: 'text-lg font-medium',
      label: 'text-xs'
    },
    md: {
      container: '',
      icon: 'w-5 h-5',
      balance: 'text-2xl font-bold',
      label: 'text-sm'
    },
    lg: {
      container: '',
      icon: 'w-6 h-6',
      balance: 'text-3xl font-bold',
      label: 'text-base'
    }
  }

  return (
    <div className={cn('flex flex-col space-y-2', className, sizeClasses[size].container)}>
      <div className="flex items-center space-x-2">
        {showIcon && <IconCoins className={cn('text-primary', sizeClasses[size].icon)} />}
        <span className={cn('text-muted-foreground', sizeClasses[size].label)}>
          {t('API Credits')}
        </span>
      </div>

      <div className="flex items-end space-x-2">
        <span className={sizeClasses[size].balance}>
          {balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </span>

        {limit && <span className="text-muted-foreground text-sm">/ {limit.toLocaleString()}</span>}
      </div>

      {showProgress && percentage !== null && (
        <div className="space-y-1">
          <Progress value={percentage} className="h-2" indicatorClassName={getProgressColor()} />
          <div className="text-muted-foreground flex justify-between text-xs">
            <span>{percentage}%</span>
            <span>{t('used')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
